import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Navigate, useNavigate, Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import TopBar from "../components/TopBar";
import CheckInPanel from "../components/CheckInPanel";
import HappinessModal from "../components/HappinessModal";
import BlockRenderer from "../components/blocks/BlockRenderer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Plus, ChevronLeft, FileText, Table, Link as LinkIcon, FileUp, Code, ListChecks, Tag, Trash2, Pencil, Image as ImageIcon, Video, Lock, ClipboardList, BarChart3, Ticket, TrendingUp, Mail, AlertTriangle, MessageCircle,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import PageFormatDialog from "../components/PageFormatDialog";
import ClientSettingsForm from "../components/ClientSettingsForm";
import ContractSummary from "../components/ContractSummary";
import EngagementProgress from "../components/EngagementProgress";
import PaymentDueReminders from "../components/PaymentDueReminders";
import SendUpdateEmailDialog from "../components/SendUpdateEmailDialog";
import PopupComposer from "../components/PopupComposer";
import ClientPopupRenderer from "../components/ClientPopupRenderer";
import SendWatiDialog from "../components/SendWatiDialog";
import ReportsView from "../components/ReportsView";
import ScopeDialog from "../components/ScopeDialog";
import { useConfirm } from "../components/ConfirmDialog";
import { toast, Toaster } from "sonner";
import { greetingFor } from "../lib/greeting";
import usePageRealtime from "../lib/usePageRealtime";
import PresenceBubbles from "../components/PresenceBubbles";

const BLOCK_TYPES = [
  { type: "text", label: "Text", icon: FileText },
  { type: "sheet", label: "Sheet", icon: Table },
  { type: "tasks_overview", label: "Tasks Overview", icon: ClipboardList },
  { type: "meta_ads", label: "Meta Ads", icon: BarChart3 },
  { type: "ticket_sales", label: "Ticket Sales", icon: Ticket },
  { type: "roas", label: "Current ROAS", icon: TrendingUp },
  { type: "link", label: "Link", icon: LinkIcon },
  { type: "file", label: "File", icon: FileUp },
  { type: "embed", label: "Embed", icon: Code },
  { type: "checklist", label: "Checklist", icon: ListChecks },
  { type: "kv", label: "KV Status", icon: Tag },
];

function defaultConfig(type) {
  switch (type) {
    case "tasks_overview":
      return { title: "Tasks overview", limit: 5 };
    case "meta_ads":
      return { title: "Active campaigns", date_preset: "last_30d", row_limit: 25, visible_metrics: ["spend", "impressions", "clicks", "ctr", "cpc", "conversions", "purchase_roas"], extra_columns: [] };
    case "ticket_sales":
      return { title: "Daily ticket sales", days: 30, view_mode: "chart" };
    case "roas":
      return { title: "Current ROAS", days: 30, goal: "" };
    case "sheet":
      return {
        title: "New sheet",
        approval_mode: "whole",
        columns: [
          { id: crypto.randomUUID(), name: "Topic", type: "text", client_writable: false },
          { id: crypto.randomUUID(), name: "Date", type: "date", client_writable: false },
          { id: crypto.randomUUID(), name: "Status", type: "status", options: [
            { value: "Draft", color: "gray" },
            { value: "Review", color: "amber" },
            { value: "Approved", color: "green" },
          ], client_writable: false },
        ],
      };
    case "checklist": return { title: "Checklist", client_writable: false };
    case "file": return { title: "Files", client_uploadable: false };
    default: return {};
  }
}

function defaultContent(type) {
  switch (type) {
    case "text": return { text: "" };
    case "checklist": return { items: [] };
    case "file": return { files: [] };
    case "link": return { label: "New link", url: "" };
    case "embed": return { url: "", html: "", mode: "url", height: 480 };
    case "media": return { url: "", kind: "image", caption: "" };
    case "kv": return { label: "Label", value: "Value" };
    default: return {};
  }
}

export default function ClientPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { confirm, promptValue } = useConfirm();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const pageId = params.get("page");

  const [client, setClient] = useState(null);
  const [clientEngagementFrozen, setClientEngagementFrozen] = useState(null);
  const [clientHasEngagement, setClientHasEngagement] = useState(false);
  // Client-role users have their whole page taken over when engagement is frozen.
  useEffect(() => {
    if (!client) { setClientEngagementFrozen(null); setClientHasEngagement(false); setClientEngagementId(null); return; }
    api.get(`/engagements`, { params: { client_id: client.id } })
      .then((r) => {
        const eng = r.data?.[0];
        setClientHasEngagement(!!eng);
        setClientEngagementId(eng?.id || null);
        setClientEngagementFrozen(!!(eng && eng.frozen && user?.role === "client" && user?.client_id === client.id));
      })
      .catch(() => { setClientEngagementFrozen(false); setClientHasEngagement(false); setClientEngagementId(null); });
  }, [client, user]);
  const [pages, setPages] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(false);
  const [overviewDraft, setOverviewDraft] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [retainerIntDraft, setRetainerIntDraft] = useState(2);
  const [retainerWorkDraft, setRetainerWorkDraft] = useState(5);
  const [logoBusy, setLogoBusy] = useState(false);

  // drag-drop state
  const [draggingId, setDraggingId] = useState(null);
  const [overState, setOverState] = useState({ id: null, edge: null });
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [internalTab, setInternalTab] = useState(false);
  const [sendUpdateOpen, setSendUpdateOpen] = useState(false);
  const [popupComposerOpen, setPopupComposerOpen] = useState(false);
  const [watiOpen, setWatiOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [clientEngagementId, setClientEngagementId] = useState(null);
  const [ratingHistory, setRatingHistory] = useState([]);

  const isTeam = user && (user.role === "admin" || user.role === "team" || user.role === "account_manager");

  const homePage = useMemo(() => pages.find((p) => p.is_home), [pages]);
  const currentPage = useMemo(() => pageId ? pages.find((p) => p.id === pageId) : homePage, [pageId, pages, homePage]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/pages`),
      ]);
      setClient(c.data);
      setPages(p.data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  // Scroll to top whenever navigating to a different client or sub-page —
  // important on mobile so the page never opens mid-scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id, pageId]);

  // Load rating history (team only) when entering the internal tab
  useEffect(() => {
    if (!isTeam || !internalTab || !client) return;
    api.get(`/clients/${client.id}/ratings`).then((r) => setRatingHistory(r.data)).catch(() => {});
  }, [internalTab, isTeam, client]);

  const [blocksLoading, setBlocksLoading] = useState(false);
  useEffect(() => {
    if (!currentPage) { setBlocks([]); return; }
    // Clear immediately so stale blocks from previous page don't flicker
    setBlocks([]);
    setBlocksLoading(true);
    const pid = currentPage.id;
    api.get(`/pages/${pid}/blocks`).then((r) => {
      // Guard against race when user clicks fast between pages
      setBlocks((prev) => (currentPage && pid === currentPage.id ? r.data : prev));
    }).catch(() => setBlocks([])).finally(() => setBlocksLoading(false));
  }, [currentPage]);

  const reloadBlocks = async () => {
    if (!currentPage) return;
    const r = await api.get(`/pages/${currentPage.id}/blocks`);
    setBlocks(r.data);
  };

  // Realtime: react to broadcasts for the current page + capture live presence.
  const { presence } = usePageRealtime(currentPage?.id, (evt) => {
    if (!evt || !currentPage) return;
    if (evt.type === "block_updated" && evt.page_id === currentPage.id) {
      setBlocks((prev) => prev.map((b) =>
        b.id === evt.block_id ? { ...b, ...evt.patch, updated_at: evt.at } : b
      ));
    }
    // Row mutations: bump a refresh key so any mounted SheetBlock for that block re-fetches
    if (evt.type === "row_created" || evt.type === "row_updated" || evt.type === "row_deleted") {
      window.dispatchEvent(new CustomEvent("tt-realtime-row", { detail: evt }));
    }
  });

  const addBlock = async (type) => {
    await api.post(`/pages/${currentPage.id}/blocks`, { type, config: defaultConfig(type), content: defaultContent(type) });
    reloadBlocks();
  };

  const updateBlock = async (bid, patch) => {
    await api.put(`/blocks/${bid}`, patch);
    reloadBlocks();
  };

  const deleteBlock = async (bid) => {
    const ok = await confirm({ title: "Delete block?", description: "This cannot be undone.", confirmText: "Delete", tone: "destructive" });
    if (!ok) return;
    await api.delete(`/blocks/${bid}`);
    reloadBlocks();
  };

  const persistOrder = async (list) => {
    await Promise.all(list.map((b, i) => api.put(`/blocks/${b.id}`, { order: i })));
    reloadBlocks();
  };

  const moveBlock = async (bid, dir) => {
    const idx = blocks.findIndex((b) => b.id === bid);
    const next = idx + dir;
    if (next < 0 || next >= blocks.length) return;
    const swapped = [...blocks];
    const tmp = swapped[idx]; swapped[idx] = swapped[next]; swapped[next] = tmp;
    setBlocks(swapped);
    persistOrder(swapped);
  };

  // Drag-drop handlers
  const onDragStart = (e, bid) => {
    setDraggingId(bid);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", bid);
  };
  const onDragOver = (e, overId) => {
    e.preventDefault();
    if (!draggingId || draggingId === overId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isTop = e.clientY < rect.top + rect.height / 2;
    setOverState({ id: overId, edge: isTop ? "top" : "bottom" });
  };
  const onDrop = (e, overId) => {
    e.preventDefault();
    if (!draggingId || draggingId === overId) { setDraggingId(null); setOverState({ id: null, edge: null }); return; }
    const fromIdx = blocks.findIndex((b) => b.id === draggingId);
    let toIdx = blocks.findIndex((b) => b.id === overId);
    if (fromIdx < 0 || toIdx < 0) return;
    const moving = blocks[fromIdx];
    const list = [...blocks];
    list.splice(fromIdx, 1);
    if (overState.edge === "bottom") toIdx = blocks.findIndex((b) => b.id === overId);
    if (fromIdx < toIdx && overState.edge === "top") toIdx -= 1;
    list.splice(toIdx, 0, moving);
    setBlocks(list);
    persistOrder(list);
    setDraggingId(null);
    setOverState({ id: null, edge: null });
  };
  const onDragEnd = () => { setDraggingId(null); setOverState({ id: null, edge: null }); };

  const addPage = async (title, format, externalUrl) => {
    const body = { title, format, parent_id: null };
    if (format === "link" && externalUrl) body.external_url = externalUrl;
    const r = await api.post(`/clients/${id}/pages`, body);
    setPages([...pages, r.data]);
    // Link-type pages don't have their own view — don't navigate into them
    if (format !== "link") setParams({ page: r.data.id });
  };

  const renamePage = async (p) => {
    const title = await promptValue({ title: "Rename page", placeholder: "Page title", defaultValue: p.title, confirmText: "Save" });
    if (!title) return;
    await api.put(`/pages/${p.id}`, { title });
    loadAll();
  };

  const editPageUrl = async (p) => {
    const url = await promptValue({
      title: "Edit external URL",
      placeholder: "https://docs.google.com/...",
      defaultValue: p.external_url || "",
      confirmText: "Save",
    });
    if (url === null || url === undefined) return;
    await api.put(`/pages/${p.id}`, { external_url: url.trim() });
    loadAll();
  };

  const deletePage = async (p) => {
    const ok = await confirm({ title: `Delete page "${p.title}"?`, description: "All blocks on this page will be permanently deleted.", confirmText: "Delete page", tone: "destructive" });
    if (!ok) return;
    await api.delete(`/pages/${p.id}`);
    if (pageId === p.id) setParams({});
    loadAll();
  };

  const saveClient = async () => {
    await api.put(`/clients/${id}`, {
      name: nameDraft, overview: overviewDraft,
      retainer_interaction_days: retainerIntDraft,
      retainer_work_days: retainerWorkDraft,
    });
    setEditingClient(false);
    loadAll();
  };

  const deleteClient = async () => {
    const ok = await confirm({
      title: `Archive client "${client?.name}"?`,
      description: "They'll move to the Archived tab — login disabled, hidden from active dashboard. All data is preserved; you can restore any time.",
      confirmText: "Archive client", tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.post(`/clients/${id}/archive`);
      toast.success("Client archived");
      navigate("/dashboard");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tt_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const f = await res.json();
      const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}`;
      await api.put(`/clients/${id}`, { logo_url: logoUrl });
      toast.success("Logo updated");
      loadAll();
    } catch (err) { toast.error("Upload failed"); }
    finally { setLogoBusy(false); e.target.value = ""; }
  };

  const onLinkNavigate = (target_page_id) => setParams({ page: target_page_id });

  if (user?.role === "client" && user.client_id !== id) {
    return <Navigate to={`/clients/${user.client_id}`} replace />;
  }

  if (loading) return (
    <div className="min-h-screen bg-[#0A0A0A]"><TopBar /><div className="p-12 text-zinc-600 text-[12px]">Loading…</div></div>
  );
  if (!client) return (
    <div className="min-h-screen bg-[#0A0A0A]"><TopBar /><div className="p-12 text-zinc-600 text-[12px]">Client not found.</div></div>
  );

  const subPages = pages.filter((p) => p.parent_id === (currentPage?.is_home ? null : currentPage?.id) && !p.is_home);
  const isHome = currentPage?.is_home;
  const isSheetPage = currentPage?.format === "sheet" || currentPage?.format === "content_calendar" || currentPage?.format === "task_tracker";
  const isReportsPage = currentPage?.format === "reports";
  const isDashboardPage = currentPage?.format === "meta_ads_dashboard" || currentPage?.format === "ticket_sales_dashboard";
  const containerCls = (isSheetPage || isDashboardPage) ? "max-w-7xl" : "max-w-5xl";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <TopBar activeClientName={client.name} activeClientLogo={client.header_logo_url} />
      <ScopeDialog open={scopeOpen} onOpenChange={setScopeOpen} client={client} isAdmin={user?.role === "admin"} onSaved={loadAll} />
      <SendUpdateEmailDialog open={sendUpdateOpen} onOpenChange={setSendUpdateOpen} client={client} currentPage={currentPage} />
      <PopupComposer open={popupComposerOpen} onOpenChange={setPopupComposerOpen}
        client={client} engagementId={clientEngagementId} />
      <SendWatiDialog open={watiOpen} onOpenChange={setWatiOpen} client={client} />
      <ClientPopupRenderer clientRef={client?.slug || client?.id}
        onFrozen={() => { window.location.reload(); }} />
      <Toaster position="top-right" theme="dark" />
      <HappinessModal />
      <div className={`${containerCls} mx-auto px-4 sm:px-6 py-6 sm:py-8 anim-fade-in`}>
        {!isHome && (
          <button onClick={() => setParams({})}
            className="flex items-center gap-2 text-[12px] text-zinc-500 hover:text-zinc-100 mb-5 transition-colors" data-testid="breadcrumb-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Home <span className="text-zinc-700 mx-1">/</span> <span className="text-zinc-200">{currentPage?.title}</span>
          </button>
        )}

        {isHome && (
          <div className="mb-8 sm:mb-10 anim-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-5">
              <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                <div className="relative group/logo">
                  {client.logo_url ? (
                    <img src={client.logo_url} alt="" className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg object-cover" />
                  ) : (
                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg bg-zinc-900 flex items-center justify-center font-display font-semibold text-[20px] sm:text-[28px] text-zinc-200">
                      {client.name?.[0]}
                    </div>
                  )}
                  {isTeam && (
                    <label className="absolute inset-0 rounded-lg bg-black/60 opacity-0 group-hover/logo:opacity-100 cursor-pointer flex items-center justify-center transition-opacity" data-testid="upload-logo">
                      <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" disabled={logoBusy} />
                      <ImageIcon className="w-5 h-5 text-white" />
                    </label>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {editingClient && isTeam ? (
                    <ClientSettingsForm
                      client={client}
                      isAdmin={user?.role === "admin"}
                      onSaved={() => { setEditingClient(false); loadAll(); }}
                      onCancel={() => setEditingClient(false)}
                      onArchive={deleteClient}
                    />
                  ) : (
                    <>
                      <div className="t-eyebrow mb-2">{greetingFor(user?.name).full}</div>
                      <h1 className="h-display-1 break-words" data-testid="client-home-title">{client.name}</h1>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Link to={`/clients/${client.slug || client.id}/details`}
                          data-testid="client-details-btn"
                          className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 rounded hover:border-zinc-600 transition-all">
                          Details
                        </Link>
                        {(user?.role === "admin" || user?.role === "accountant" || user?.role === "account_manager"
                          || (user?.role === "client" && user?.client_id === client.id)) && (
                          <Link to={`/clients/${client.slug || client.id}/payments`}
                            data-testid="client-payments-btn"
                            className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 rounded hover:border-zinc-600 transition-all">
                            Payments
                          </Link>
                        )}
                        {(isTeam || (user?.role === "client" && client?.resources_enabled)) && (
                          <Link to={`/clients/${client.slug || client.id}/resources`}
                            data-testid="client-resources-btn"
                            className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 rounded hover:border-zinc-600 transition-all">
                            Resources
                          </Link>
                        )}
                        <Link to={`/clients/${client.slug || client.id}/requirements`}
                          data-testid="client-requirements-btn"
                          className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 rounded hover:border-zinc-600 transition-all">
                          Requirements
                        </Link>
                        <Link to={`/clients/${client.slug || client.id}/requests`}
                          data-testid="client-requests-btn"
                          className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 border border-zinc-800 rounded hover:border-zinc-600 transition-all">
                          Requests
                        </Link>
                      </div>
                      <p className="text-zinc-400 mt-2 max-w-2xl text-[13px] leading-relaxed">
                        {client.overview || (isTeam ? <em className="text-zinc-700">Add an overview…</em> : "")}
                      </p>
                    </>
                  )}
                </div>
              </div>
              {!isTeam && (
                <PresenceBubbles presence={presence} className="shrink-0" />
              )}
              {isTeam && !editingClient && (
                <div className="flex flex-wrap sm:flex-col sm:items-end items-center gap-2 shrink-0">
                  <PresenceBubbles presence={presence} className="mb-1 sm:mb-2" />
                  <Button variant="ghost" size="sm" onClick={() => { setEditingClient(true); setNameDraft(client.name); setOverviewDraft(client.overview || ""); setRetainerIntDraft(client.retainer_interaction_days || 2); setRetainerWorkDraft(client.retainer_work_days || 5); }} className="text-zinc-500 hover:text-white text-[12px] h-8" data-testid="edit-client-btn">
                    <Pencil className="w-3 h-3 mr-1.5" /> Client settings
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setScopeOpen(true)} data-testid="scope-btn"
                    className="text-zinc-500 hover:text-white text-[12px] h-8">
                    <FileText className="w-3 h-3 mr-1.5" /> Scope
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSendUpdateOpen(true)} data-testid="send-update-btn"
                    className="text-zinc-500 hover:text-white text-[12px] h-8">
                    <Mail className="w-3 h-3 mr-1.5" /> Send update
                  </Button>
                  {(user?.role === "admin" || user?.role === "account_manager") && (
                    <Button variant="ghost" size="sm" onClick={() => setPopupComposerOpen(true)} data-testid="send-popup-btn"
                      className="text-zinc-500 hover:text-white text-[12px] h-8">
                      <AlertTriangle className="w-3 h-3 mr-1.5" /> Send popup
                    </Button>
                  )}
                  {(user?.role === "admin" || user?.role === "account_manager") && (
                    <Button variant="ghost" size="sm" onClick={() => setWatiOpen(true)} data-testid="send-wati-btn"
                      className="text-zinc-500 hover:text-emerald-400 text-[12px] h-8">
                      <MessageCircle className="w-3 h-3 mr-1.5" /> WhatsApp
                    </Button>
                  )}
                  <button onClick={() => setInternalTab((v) => !v)}
                    data-testid="tt-only-tab"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.18em] font-mono transition-all border ${
                      internalTab
                        ? "bg-white text-black border-white"
                        : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
                    }`}>
                    <Lock className="w-2.5 h-2.5" /> T&amp;T only
                  </button>
                </div>
              )}
            </div>

            {/* Payment due reminders — banners for 2-3 days away, modal popup for ≤1 day / overdue */}
            {client?.id && (
              <div className="mt-6" data-testid="payment-reminders-slot">
                <PaymentDueReminders clientId={client.id} />
              </div>
            )}

            {/* Progress section — visible to every role; replaces the ContractSummary
                fallback if an engagement exists for this client. */}
            <div className="mt-6" data-testid="progress-slot">
              <EngagementProgress clientId={client.id} />
            </div>

            {/* If the client-role user's engagement is frozen, we HIDE the rest of the
                client dashboard below and let EngagementProgress render the takeover. */}
            {clientEngagementFrozen ? null : (
              <>
                {/* Legacy Contract summary — kept ONLY for admin/accountant when NO engagement exists.
                    Once an engagement is present the money data lives inside EngagementProgress + the
                    dedicated /payments page. Client role never sees this on the main dashboard. */}
                {(user?.role === "admin" || user?.role === "accountant") && !clientHasEngagement && (
                  <div className="mt-6" data-testid="contract-summary-fallback">
                    <ContractSummary client={client} />
                  </div>
                )}
              </>
            )}

            {clientEngagementFrozen ? null : (subPages.length > 0 || (client?.resources_enabled && user?.role === "client")) && (
              <>
                <div className="mt-8 mb-3 flex items-center justify-between">
                  <div className="t-eyebrow flex items-center gap-1.5">
                    <FileText className="w-3 h-3 text-zinc-400" /> Documents & pages
                  </div>
                  <span className="text-[10px] font-mono text-zinc-600">
                    {subPages.length + (client?.resources_enabled ? 1 : 0)} item{(subPages.length + (client?.resources_enabled ? 1 : 0)) !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 stagger">
                  {client?.resources_enabled && (user?.role === "client" || isTeam) && (
                    <div key="__resources" className="group relative anim-fade-up">
                      <button onClick={() => navigate(`/clients/${client.slug || client.id}/resources`)}
                        data-testid="subpage-resources-card"
                        className="w-full text-left tt-card p-4 hover:translate-y-[-2px] flex items-center gap-3 border-emerald-900/40 bg-emerald-950/10">
                        <div className="w-9 h-9 rounded-lg bg-emerald-950/40 border border-emerald-900/50 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-emerald-300" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display text-[13px] font-semibold text-zinc-100 truncate">Resources</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5 truncate font-mono">
                            Shared PDFs, decks & links →
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                {subPages.map((p) => {
                  const isLink = p.format === "link" && p.external_url;
                  let host = null;
                  if (isLink) {
                    try {
                      host = new URL(p.external_url.startsWith("http") ? p.external_url : `https://${p.external_url}`).hostname;
                    } catch { host = p.external_url; }
                  }
                  const handleClick = () => {
                    if (isLink) {
                      const u = p.external_url;
                      window.open(u.startsWith("http") ? u : `https://${u}`, "_blank", "noopener,noreferrer");
                    } else {
                      setParams({ page: p.id });
                    }
                  };
                  return (
                    <div key={p.id} className="group relative anim-fade-up">
                      <button onClick={handleClick}
                        data-testid={`subpage-link-${p.id}`}
                        title={isLink ? p.external_url : undefined}
                        className="w-full text-left tt-card p-4 hover:translate-y-[-2px] flex items-center gap-3">
                        {isLink && (
                          <div className="w-9 h-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                            <img src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`}
                              alt="" className="w-6 h-6"
                              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-zinc-500"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>'; }} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-display text-[13px] font-semibold text-zinc-100 truncate">{p.title}</div>
                          <div className="text-[11px] text-zinc-600 mt-0.5 truncate font-mono">
                            {isLink ? `${host} ↗` : "Open page →"}
                          </div>
                        </div>
                      </button>
                      {/* Hover URL preview (link pages only) */}
                      {isLink && (
                        <div className="pointer-events-none absolute left-0 right-0 -bottom-2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity z-30"
                          data-testid={`subpage-link-tooltip-${p.id}`}>
                          <div className="bg-[#0a0a0a] border border-zinc-800 rounded-md shadow-xl px-3 py-2 text-[11px] text-zinc-300 font-mono break-all max-w-full">
                            {p.external_url}
                          </div>
                        </div>
                      )}
                      {isTeam && (
                        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 flex">
                          {isLink && (
                            <Button size="sm" variant="ghost" onClick={() => editPageUrl(p)}
                              className="h-6 w-6 p-0 text-zinc-500 hover:text-emerald-400"
                              title="Edit URL"
                              data-testid={`subpage-edit-url-${p.id}`}>
                              <LinkIcon className="w-2.5 h-2.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => renamePage(p)} className="h-6 w-6 p-0 text-zinc-500" title="Rename"><Pencil className="w-2.5 h-2.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deletePage(p)} className="h-6 w-6 p-0 text-zinc-500 hover:text-[#EF4444]" title="Delete"><Trash2 className="w-2.5 h-2.5" /></Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </>
            )}
            {isTeam && (
              <Button onClick={() => setPageDialogOpen(true)} variant="ghost" size="sm" className="mt-3 text-zinc-500 hover:text-white text-[12px] h-8" data-testid="new-page-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add page
              </Button>
            )}
          </div>
        )}

        <PageFormatDialog open={pageDialogOpen} onOpenChange={setPageDialogOpen} onCreate={addPage} />

        {/* T&T internal-only panel — replaces the workspace body when active */}
        {isTeam && internalTab && isHome && (
          <div className="mt-8 anim-fade-up space-y-5">
            <div className="flex items-center gap-2 t-eyebrow text-zinc-300">
              <Lock className="w-3 h-3" /> Thick &amp; Thin internal · client deep-dive
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="tt-card p-3.5">
                <div className="t-eyebrow text-[9px]">Created</div>
                <div className="font-display text-[15px] font-semibold text-zinc-100 mt-1">{client.created_at ? new Date(client.created_at).toLocaleDateString() : "—"}</div>
                <div className="text-[10px] text-zinc-600 font-mono mt-0.5">{client.created_at ? new Date(client.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
              </div>
              <div className="tt-card p-3.5">
                <div className="t-eyebrow text-[9px]">URL slug</div>
                <div className="font-mono text-[12px] text-zinc-100 mt-1 truncate">/{client.slug || client.id}</div>
                <div className="text-[10px] text-zinc-600 mt-0.5">share link</div>
              </div>
              <div className="tt-card p-3.5">
                <div className="t-eyebrow text-[9px]">Retainer · interaction</div>
                <div className="font-display text-[15px] font-semibold text-zinc-100 mt-1">{client.retainer_interaction_days || 2}d</div>
                <div className="text-[10px] text-zinc-600 mt-0.5">red-flag threshold</div>
              </div>
              <div className="tt-card p-3.5">
                <div className="t-eyebrow text-[9px]">Retainer · delivery</div>
                <div className="font-display text-[15px] font-semibold text-zinc-100 mt-1">{client.retainer_work_days || 5}d</div>
                <div className="text-[10px] text-zinc-600 mt-0.5">red-flag threshold</div>
              </div>
            </div>

            <div className="tt-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="t-eyebrow">Weekly rating history</div>
                <span className="text-[11px] text-zinc-600 font-mono">{ratingHistory.length} entries</span>
              </div>
              {ratingHistory.length === 0 ? (
                <div className="text-zinc-600 text-[12px]">No ratings recorded yet.</div>
              ) : (
                <ul className="space-y-2">
                  {ratingHistory.map((r) => {
                    const cls = r.rating >= 8 ? "score-good" : r.rating >= 7 ? "score-okay" : "score-bad";
                    return (
                      <li key={r.id} className="flex items-start gap-3 py-2 border-b border-zinc-900 last:border-0">
                        <span className={`px-2 py-1 rounded text-[12px] font-mono shrink-0 ${cls}`}>{r.rating}/10</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-zinc-500 font-mono">
                            {new Date(r.created_at).toLocaleString()} · {r.user_name}
                            <span className="ml-2 uppercase tracking-wider text-[10px]">week of {r.week_start}</span>
                          </div>
                          {r.note && <div className="text-[13px] text-zinc-300 mt-1 leading-relaxed">"{r.note}"</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="text-[11px] text-zinc-700 font-mono uppercase tracking-wider px-1">
              Visible to admin and team only · client never sees this view
            </div>
          </div>
        )}

        {!isHome && (
          <div className="mb-7 anim-fade-up">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h1 className="h-display-1" data-testid="subpage-title">{currentPage?.title}</h1>
              <PresenceBubbles presence={presence} />
            </div>
            {isTeam && (
              <div className="flex gap-2 mt-2.5">
                <Button variant="ghost" size="sm" onClick={() => renamePage(currentPage)} className="text-zinc-500 hover:text-white text-[12px] h-8"><Pencil className="w-3 h-3 mr-1" /> Rename</Button>
                <Button variant="ghost" size="sm" onClick={() => deletePage(currentPage)} className="text-zinc-500 hover:text-[#EF4444] text-[12px] h-8"><Trash2 className="w-3 h-3 mr-1" /> Delete page</Button>
              </div>
            )}
          </div>
        )}

        {clientEngagementFrozen ? null : !(isTeam && internalTab && isHome) && (
        isReportsPage ? <ReportsView clientId={client.id} /> :
        <div className={`grid ${isTeam ? "grid-cols-1 lg:grid-cols-[1fr_280px]" : "grid-cols-1"} gap-5 sm:gap-7`}>
          {isTeam && (
            <div className="lg:hidden order-first">
              <CheckInPanel clientId={id} />
            </div>
          )}
          <div className="space-y-5 sm:space-y-6 min-w-0 stagger">
            {blocks.map((b) => (
              <BlockRenderer key={b.id} block={b} isTeam={isTeam}
                onUpdateBlock={updateBlock} onDeleteBlock={deleteBlock} onMove={moveBlock}
                onNavigate={onLinkNavigate}
                onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}
                isDragging={draggingId === b.id}
                dragOver={overState.id === b.id ? overState.edge : null}
              />
            ))}
            {blocks.length === 0 && (
              <div className="text-zinc-700 text-[12px] border border-dashed border-zinc-800 rounded-lg p-8 text-center">
                {isTeam ? "No blocks yet. Add one below." : "Nothing here yet."}
              </div>
            )}
            {isTeam && (currentPage?.format == null || currentPage?.format === "mixed") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-zinc-500 hover:text-white border border-dashed border-zinc-800 hover:border-zinc-600 w-full justify-start text-[12px] h-9" data-testid="add-block-btn">
                    <Plus className="w-3.5 h-3.5 mr-2" /> Add block
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#111] border-zinc-800">
                  {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
                    <DropdownMenuItem key={type} onClick={() => addBlock(type)} className="cursor-pointer text-[12px]" data-testid={`add-block-${type}`}>
                      <Icon className="w-3.5 h-3.5 mr-2 text-zinc-400" /> {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {isTeam && (
            <div className="hidden lg:block">
              <CheckInPanel clientId={id} />
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
